import { useState, useEffect, useMemo } from "react";
import { Search, Loader2, Library, SlidersHorizontal, ListChecks, Palette,
  ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { PokemonCardTile } from "./PokemonCardTile";
import { AddToListButtons } from "./AddToListButtons";
import { computeVariants } from "../lib/variants";
import { VARIANT_ORDER, LABELS, TCGP_ONLY_RARITIES } from "../config/variants";
import { getCollectionKey } from "../lib/collection";
import { buildSidebarBlocs } from "../lib/sidebar";
import { fetchFilterOptions, fetchCrossViewCards } from "../lib/api";

const CATEGORIES = ["Pokémon", "Dresseur", "Énergie"];

// Vue transversale (Phase 4) : choisir un ou plusieurs blocs/séries + une ou
// plusieurs raretés (obligatoire, sinon la liste serait énorme) et voir
// toutes les cartes correspondantes mélangées, groupées par set, avec la
// progression sur ce sous-ensemble précis.
//
// Pokémon TCG Pocket (série "tcgp") est volontairement exclu de cet écran :
// son système de raretés (Diamant/Étoile/Couronne) n'a rien à voir avec le
// TCG physique et polluerait le sélecteur. Il reste accessible normalement
// ailleurs dans l'appli (sidebar, Explorer les cartes).
export const CrossRarityView = ({
  seriesData, collection, conditions = {}, onStatusChange, onRemove, onCardClick,
  lists = [], onCreateList, onAddToList,
}) => {
  const tcgpSetIds = useMemo(() => {
    const serie = seriesData.find(s => s.id === "tcgp");
    return new Set((serie?.sets || []).map(s => s.id));
  }, [seriesData]);

  const blocs = useMemo(() => {
    return buildSidebarBlocs(seriesData)
      .map(bloc => ({
        ...bloc,
        sets: bloc.sets.filter(s => !tcgpSetIds.has(s.id)),
        subBlocs: bloc.subBlocs
          ?.map(sub => ({ ...sub, sets: sub.sets.filter(s => !tcgpSetIds.has(s.id)) }))
          .filter(sub => sub.sets.length > 0),
      }))
      .filter(bloc => bloc.sets.length > 0);
  }, [seriesData, tcgpSetIds]);

  const [selectedSetKeys, setSelectedSetKeys] = useState(() => new Set());
  const [expandedBlocs, setExpandedBlocs] = useState(() => new Set());
  const [selectedRarities, setSelectedRarities] = useState(() => new Set());
  const [variant, setVariant] = useState(null);
  const [category, setCategory] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ rarities: [], categories: [] });
  const [cards, setCards] = useState(null); // null = recherche pas encore lancée
  const [loading, setLoading] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkVariant, setBulkVariant] = useState("normal");

  useEffect(() => {
    fetchFilterOptions().then(setFilterOptions).catch(err => console.error(err));
  }, []);

  // Raretés propres au TCG physique — celles de TCG Pocket sont retirées ici,
  // pas dans fetchFilterOptions (utilisé aussi par "Explorer les cartes", qui
  // doit lui pouvoir chercher des cartes Pocket).
  const availableRarities = useMemo(
    () => filterOptions.rarities.filter(r => !TCGP_ONLY_RARITIES.has(r)),
    [filterOptions]
  );

  // La sélection se fait au SET : cocher un bloc n'est qu'un raccourci pour
  // cocher tous ses sets. Ça permet de ne prendre qu'une série précise dans un
  // bloc (ex. juste "Destinées Occultes" sans tout Soleil et Lune).
  const blocState = (bloc) => {
    const ids = bloc.sets.map(s => s.id);
    const n = ids.filter(id => selectedSetKeys.has(id)).length;
    return n === 0 ? "none" : n === ids.length ? "all" : "partial";
  };

  const toggleBloc = (bloc) => {
    const ids = bloc.sets.map(s => s.id);
    const everything = blocState(bloc) === "all";
    setSelectedSetKeys(prev => {
      const next = new Set(prev);
      ids.forEach(id => everything ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const toggleSet = (setId) => {
    setSelectedSetKeys(prev => {
      const next = new Set(prev);
      if (next.has(setId)) next.delete(setId);
      else next.add(setId);
      return next;
    });
  };

  const toggleExpanded = (key) => {
    setExpandedBlocs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSetIds = useMemo(() => blocs.flatMap(b => b.sets.map(s => s.id)), [blocs]);
  const allBlocsSelected = allSetIds.length > 0 && allSetIds.every(id => selectedSetKeys.has(id));
  const toggleAllBlocs = () => {
    setSelectedSetKeys(allBlocsSelected ? new Set() : new Set(allSetIds));
  };

  const toggleRarity = (r) => {
    setSelectedRarities(prev => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  // Ordre des blocs conservé (plus lisible que l'ordre de cochage).
  const selectedSetIds = useMemo(
    () => blocs.flatMap(b => b.sets.filter(s => selectedSetKeys.has(s.id)).map(s => s.id)),
    [blocs, selectedSetKeys]
  );

  const canSearch = selectedSetIds.length > 0 && selectedRarities.size > 0;

  const runSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    try {
      const data = await fetchCrossViewCards({
        setIds: selectedSetIds, rarity: Array.from(selectedRarities), variant, category,
      });
      setCards(data.cards || []);
    } catch (err) {
      console.error("Error fetching cross-view cards:", err);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  // Groupé par set, dans l'ordre des blocs cochés (plus lisible que tout
  // mélanger sans repère quand on couvre plusieurs séries à la fois).
  const groups = useMemo(() => {
    if (!cards) return [];
    const bySet = {};
    cards.forEach(c => {
      if (!bySet[c.set_id]) bySet[c.set_id] = { set_id: c.set_id, set_name: c.set_name, cards: [] };
      bySet[c.set_id].cards.push(c);
    });
    return selectedSetIds.map(id => bySet[id]).filter(Boolean);
  }, [cards, selectedSetIds]);

  const progress = useMemo(() => {
    if (!cards) return { owned: 0, total: 0 };
    let owned = 0, total = 0;
    cards.forEach(card => {
      if (variant) {
        const types = computeVariants(card).filter(v => v.type !== "loading").map(v => v.type);
        if (!types.includes(variant)) return;
        total++;
        if (collection[getCollectionKey(card.id, variant)] === "owned") owned++;
      } else {
        total++;
        const types = ["normal", "holo", "cosmos", "rgb", "reverse", "firstEdition", "wPromo"];
        const statuses = types.map(t => collection[getCollectionKey(card.id, t)]).filter(Boolean);
        if (statuses.includes("owned")) owned++;
      }
    });
    return { owned, total };
  }, [cards, collection, variant]);

  const completionPct = progress.total > 0 ? Math.round(progress.owned / progress.total * 100) : 0;

  const handleVariantClick = (cardId, variantType, currentStatus, shiftKey) => {
    if (shiftKey) {
      if (currentStatus === "wanted") onRemove(cardId, variantType);
      else onStatusChange(cardId, variantType, "wanted");
    } else {
      if (!currentStatus || currentStatus === "wanted") onStatusChange(cardId, variantType, "owned");
      else onRemove(cardId, variantType);
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleCardSelection = (cardId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const selectedItems = () => Array.from(selectedIds)
    .map(cardId => (cards || []).find(c => c.id === cardId))
    .filter(card => card && computeVariants(card).some(v => v.type === bulkVariant))
    .map(card => ({ card_id: card.id, variant: bulkVariant }));

  // Voir SetDetailView : on garde la sélection pour pouvoir ajouter
  // plusieurs fois / à plusieurs listes (compteur sur le bouton).
  const addSelectionToList = async (listId) => {
    await onAddToList(listId, selectedItems());
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-2">
        <SlidersHorizontal className="w-6 h-6 text-red-500" />Vue par rareté
      </h2>

      <div className="mb-5 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-zinc-400">Blocs / séries</div>
          <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white h-6 px-2"
            onClick={toggleAllBlocs}>
            {allBlocsSelected ? "Tout désélectionner" : "Tout sélectionner"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {blocs.map(bloc => {
            const state = blocState(bloc);
            const open = expandedBlocs.has(bloc.key);
            return (
              <div key={bloc.key} className="flex">
                <Button size="sm" variant="outline"
                  className={`border-zinc-700 text-white rounded-r-none ${
                    state === "all" ? "bg-zinc-800"
                    : state === "partial" ? "bg-zinc-800/40 border-emerald-700" : ""}`}
                  onClick={() => toggleBloc(bloc)}>
                  {bloc.label}
                  {state === "partial" && (
                    <span className="ml-1.5 text-[0.65rem] text-emerald-400">
                      {bloc.sets.filter(s => selectedSetKeys.has(s.id)).length}/{bloc.sets.length}
                    </span>
                  )}
                </Button>
                <Button size="sm" variant="outline"
                  title="Choisir les séries une par une"
                  className="border-zinc-700 border-l-0 text-zinc-400 rounded-l-none px-1.5"
                  onClick={() => toggleExpanded(bloc.key)}>
                  {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </Button>
              </div>
            );
          })}
        </div>
        {blocs.filter(b => expandedBlocs.has(b.key)).map(bloc => (
          <div key={bloc.key} className="mb-3 pl-3 border-l-2 border-zinc-800">
            <div className="text-xs text-zinc-500 mb-1.5">{bloc.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {bloc.sets.map(set => (
                <button key={set.id}
                  onClick={() => toggleSet(set.id)}
                  className={`text-xs rounded px-2 py-1 border ${selectedSetKeys.has(set.id)
                    ? "bg-emerald-500/15 border-emerald-600 text-emerald-300"
                    : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:text-white"}`}>
                  {set.name}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="mb-4" />

        <div className="text-sm text-zinc-400 mb-2">
          Raretés {selectedRarities.size > 0 && <span className="text-zinc-600">({selectedRarities.size} sélectionnée{selectedRarities.size > 1 ? "s" : ""})</span>}
        </div>
        <div className="flex flex-wrap gap-2 mb-4 max-h-32 overflow-y-auto pr-1">
          {availableRarities.map(r => (
            <Button key={r} size="sm" variant="outline"
              className={`border-zinc-700 text-white ${selectedRarities.has(r) ? "bg-zinc-800" : ""}`}
              onClick={() => toggleRarity(r)}>
              {r}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className={`border-zinc-700 text-white ${variant ? "bg-zinc-800" : ""}`}>
                {variant ? LABELS[variant] : "Tous les variants"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white">
              <DropdownMenuItem onClick={() => setVariant(null)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                Tous les variants
              </DropdownMenuItem>
              {VARIANT_ORDER.map(type => (
                <DropdownMenuItem key={type} onClick={() => setVariant(type)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                  {LABELS[type]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {CATEGORIES.map(cat => (
            <Button key={cat} size="sm" variant="outline"
              className={`border-zinc-700 text-white ${category === cat ? "bg-zinc-800" : ""}`}
              onClick={() => setCategory(category === cat ? null : cat)}>
              {cat}
            </Button>
          ))}

          <div className="flex-1" />
          <Button size="sm" disabled={!canSearch || loading}
            className="bg-red-600 hover:bg-red-500 disabled:opacity-40"
            onClick={runSearch}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Afficher
          </Button>
        </div>
        {!canSearch && (
          <div className="text-xs text-zinc-500 mt-2">
            Coche au moins un bloc et une rareté pour lancer la recherche.
          </div>
        )}
      </div>

      {cards !== null && (
        <>
          <div className="mb-5 bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-400">
                {progress.owned} / {progress.total} possédées
                {variant ? ` (variant ${LABELS[variant]})` : ""}
              </span>
              <span className="text-lg font-bold text-emerald-400">{completionPct}%</span>
            </div>
            <Progress value={completionPct} className="h-2 bg-zinc-800" />
          </div>

          {cards.length > 0 && (
            <div className="flex justify-end mb-3">
              <Button size="sm" variant="outline"
                className={`border-zinc-700 text-white ${selectMode ? "bg-blue-500/20 border-blue-500 text-blue-400" : ""}`}
                onClick={toggleSelectMode}>
                <ListChecks className="w-4 h-4 mr-2" />
                {selectMode ? "Quitter la sélection" : "Mode sélection"}
              </Button>
            </div>
          )}

          {selectMode && (
            <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <span className="text-sm text-zinc-400">{selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                    <Palette className="w-4 h-4 mr-2" />Variant : {LABELS[bulkVariant] || bulkVariant}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white">
                  {VARIANT_ORDER.map(type => (
                    <DropdownMenuItem key={type} onClick={() => setBulkVariant(type)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                      {LABELS[type]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="w-full">
                <AddToListButtons lists={lists} disabled={selectedIds.size === 0}
                  onAddToList={addSelectionToList} onCreateList={onCreateList} />
              </div>
            </div>
          )}

          {groups.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <Library className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucune carte trouvée.</p>
            </div>
          ) : (
            groups.map(group => (
              <div key={group.set_id} className="mb-8">
                <h3 className="text-sm font-semibold text-zinc-300 mb-3 uppercase tracking-wide">
                  {group.set_name} <span className="text-zinc-600">({group.cards.length})</span>
                </h3>
                <div className="card-grid">
                  {group.cards.map(card => (
                    <PokemonCardTile
                      key={card.id}
                      card={card}
                      collectionMap={collection}
                      conditionMap={conditions}
                      onCardClick={onCardClick}
                      onVariantClick={handleVariantClick}
                      cardVariants={computeVariants(card)}
                      selectMode={selectMode}
                      selected={selectedIds.has(card.id)}
                      onToggleSelect={toggleCardSelection}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};
